import { FeatureFlagEnum } from '../../../../featureflag/feature-flag.enum';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { useAppEnvironmentStore } from '../../../../stores/AppEnvironment/appEnvironment.store';
import usePaymentReleaseSettings from '../../../../stores/GeneralSettings/hooks/usePaymentReleaseSettings';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { useAuthContext } from '../../../login/AuthContext';
import { useNetworkContext } from '../../../offline/contexts/network/network.context';
import { useAbility } from '../../../users/AbilityContext';
import { OrderActions } from '../../actions/OrderActions';
import { GetActionsParams, PrimaryActionParams, Role } from '../../actions/types/actions.types';
import { ActionEnum } from '../../actions/types/orderActions.types';
import { getLastOrderGatewayPayment } from '../../actions/utils/orderSummaryAction.utils';
import { OrderType } from '../../order.types';
import { useOrderContext } from '../Order.context';
import { useOrderConditionContext } from '../OrderCondition.context';

const useOrderSummary = () => {
  const ability = useAbility();
  const { order } = useOrderContext();
  const { loggedin } = useAuthContext();
  const { userHasInternet } = useNetworkContext();
  const { orderConditions } = useOrderConditionContext();
  const { isEnableShareSuggestedOrder, sellerCanCreateOrChangeSuggestedOrder, getFeatureFlag } =
    useFeatureFlag();
  const appEnvironment = useAppEnvironmentStore((state) => state.appEnvironment);
  const {
    userRole,
    erpIntegration,
    state: { brands },
    hasIntegrations,
    hasOrderIntegration,
    canForceReverseOrderSync,
  } = useApplicationContext();

  const { requirePaymentReleaseForImmediate, requirePaymentReleaseForPreOrder, refundLimitHours } =
    usePaymentReleaseSettings();

  const getOrderSummary = () => {
    if (!order) {
      return { primaryAction: undefined, moreActions: [] };
    }

    const lastGatewayPayment = getLastOrderGatewayPayment(order.orderGatewayPayments);

    const orderPageActionsParams: GetActionsParams = {
      userAbility: ability,
      hasSplit: !!order?.splitSettings.length,
      isEnableShareSuggestedOrder,
      sellerCanCreateOrChangeSuggestedOrder,
      splitted: order?.splitted,
      status: order?.status,
      archivedOrder: order?.archived,
      hasIntegrations,
      synchronized: order?.synchronized,
      triggerIntegrationConfig: erpIntegration?.integrationTrigger,
      integrationName: erpIntegration?.name,
      canForceReverseOrderSync,
      brand: brands[0],
      role: userRole as Role,
      loggedin,
      isSystemParamsResetIntegration: getFeatureFlag(FeatureFlagEnum.RESET_INTEGRATION, false),
      isEnableAcceptSuggestedOrder: getFeatureFlag(FeatureFlagEnum.ACCEPT_SUGGESTED_ORDER, true),
      orderPaymentStatus: order?.paymentStatus,
      hasOrderIntegration,
      userHasNotInternet: !userHasInternet,
      lastGatewayPaymentStatus: lastGatewayPayment?.status,
      lastGatewayPaymentUpdatedAt: lastGatewayPayment?.updatedAt,
      refundLimitHours,
      orderType: order?.type as OrderType,
      requirePaymentGateway:
        orderConditions.length === 1 ? !!orderConditions[0].payment?.requirePaymentGateway : false,
      requirePaymentReleaseForImmediate,
      requirePaymentReleaseForPreOrder,
      appEnvironment,
      removeActions: [ActionEnum.VIEW_ORDER_ACTIVITIES],
    };

    const primaryActionParams: PrimaryActionParams = {
      appEnvironment,
      userHasInternet,
      role: userRole as Role,
      status: order?.status,
    };

    const orderActions = new OrderActions();
    return {
      primaryAction: orderActions.getPrimaryAction(primaryActionParams),
      moreActions: orderActions.getActions(orderPageActionsParams),
    };
  };

  const summary = getOrderSummary();

  return summary;
};

export default useOrderSummary;
